import { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress, Typography, Divider,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getPurchaseOrder, type PurchaseOrderListItem } from '../../api/comenziFurnizor'
import { createGoodsReceipt, type SaveGoodsReceiptLinePayload, type GoodsQualityStatus } from '../../api/receptii'
import { getWarehouse } from '../../api/depozite'
import { errorMessage } from '../../api/client'

interface LineInput { quantityReceived: string; lotNumber: string; zoneId: string; locationId: string; qualityStatus: GoodsQualityStatus }

export default function ReceiveGoodsDialog({ order, onClose, onReceived }: {
  order: PurchaseOrderListItem | null; onClose: () => void; onReceived: () => void
}) {
  const open = order !== null
  const qc = useQueryClient()
  const { data: detail } = useQuery({
    queryKey: ['achizitii', 'comenzi', order?.id ?? null], queryFn: () => getPurchaseOrder(order!.id), enabled: open,
  })
  const { data: warehouse } = useQuery({
    queryKey: ['stocuri', 'depozit', detail?.deliveryWarehouseId ?? null], queryFn: () => getWarehouse(detail!.deliveryWarehouseId),
    enabled: !!detail?.deliveryWarehouseId,
  })
  const today = new Date().toISOString().slice(0, 10)
  const [receivedDate, setReceivedDate] = useState(today)
  const [inputs, setInputs] = useState<Record<string, LineInput>>({})

  useEffect(() => {
    if (detail) {
      setReceivedDate(today)
      setInputs(Object.fromEntries(detail.lines.map((l) => [l.id!, { quantityReceived: '', lotNumber: '', zoneId: '', locationId: '', qualityStatus: 'Acceptat' as GoodsQualityStatus }])))
    }
  }, [detail, today])

  const zones = warehouse?.zones ?? []

  const mutation = useMutation({
    mutationFn: () => {
      const lines: SaveGoodsReceiptLinePayload[] = Object.entries(inputs)
        .filter(([, v]) => Number(v.quantityReceived) > 0)
        .map(([lineId, v]) => ({
          purchaseOrderLineId: lineId, quantityReceived: Number(v.quantityReceived), lotNumber: v.lotNumber || null,
          zoneId: v.zoneId || null, locationId: v.locationId || null, qualityStatus: v.qualityStatus,
        }))
      return createGoodsReceipt({ receivedDate, purchaseOrderId: order!.id, warehouseId: detail!.deliveryWarehouseId, notes: null, lines })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['achizitii', 'comenzi'] })
      void qc.invalidateQueries({ queryKey: ['achizitii', 'receptii'] })
      onReceived()
      onClose()
    },
  })

  const update = (lineId: string, patch: Partial<LineInput>) =>
    setInputs((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...patch } }))

  const hasAnyQuantity = Object.values(inputs).some((v) => Number(v.quantityReceived) > 0)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Recepție marfă — comandă {order?.number}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField type="date" label="Data recepției" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)}
            sx={{ width: 180 }} slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
            error={receivedDate > today} helperText={receivedDate > today ? 'Data nu poate fi în viitor.' : undefined} />
          {detail?.lines.filter((l) => l.quantityRemaining > 0).map((l) => {
            const input = inputs[l.id!]
            if (!input) return null
            const selectedZone = zones.find((z) => z.id === input.zoneId)
            return (
              <Stack key={l.id} spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {l.articleCode} — {l.articleDescription} (rest: {l.quantityRemaining} {l.unitOfMeasureCode})
                </Typography>
                <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                  <TextField type="number" size="small" label="Cantitate recepționată" sx={{ width: 170 }}
                    value={input.quantityReceived} onChange={(e) => update(l.id!, { quantityReceived: e.target.value })} />
                  <TextField size="small" label="Nr. lot" sx={{ width: 150 }}
                    value={input.lotNumber} onChange={(e) => update(l.id!, { lotNumber: e.target.value })} />
                  <TextField select size="small" label="Zonă" sx={{ width: 160 }}
                    value={input.zoneId} onChange={(e) => update(l.id!, { zoneId: e.target.value, locationId: '' })}>
                    <MenuItem value="">—</MenuItem>
                    {zones.map((z) => <MenuItem key={z.id} value={z.id!}>{z.name}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Locație" sx={{ width: 160 }} disabled={!selectedZone}
                    value={input.locationId} onChange={(e) => update(l.id!, { locationId: e.target.value })}>
                    <MenuItem value="">—</MenuItem>
                    {selectedZone?.locations.map((loc) => <MenuItem key={loc.id} value={loc.id!}>{loc.code}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Calitate" sx={{ width: 130 }}
                    value={input.qualityStatus} onChange={(e) => update(l.id!, { qualityStatus: e.target.value as GoodsQualityStatus })}>
                    <MenuItem value="Acceptat">Acceptat</MenuItem>
                    <MenuItem value="InControl">În control</MenuItem>
                    <MenuItem value="Respins">Respins</MenuItem>
                  </TextField>
                </Stack>
                <Divider />
              </Stack>
            )
          })}
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!hasAnyQuantity || mutation.isPending} onClick={() => mutation.mutate()}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Înregistrează recepția
        </Button>
      </DialogActions>
    </Dialog>
  )
}
